import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, Button, Input, CircularProgress } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import styles from "./staffmanage.module.css";
import { PART_VAR_FIELDS } from "./contractvarkeys";
import { API, toastOptions, errorText } from "./rosterutils";

//개인 변수 조정 — 직무 하나. 회색 = 기본값 그대로, 노란색 = 이 직원·이 파트만 다르게 저장된 값 (다음 달 발송에도 유지)
//지점 정보(대표자·소재지·법인)는 여기 없음 (계약서 변수 관리에서만)
const SALARY_KEYS = [
    { key: "common.hourlyWage", label: "시급" },
    { key: "common.fullAttendanceAllowance", label: "만근수당" },
    { key: "common.baseHourlyWage", label: "기본시급" }
];
const WORK_KEYS = PART_VAR_FIELDS.map((f) => ({ key: `part.${f.key}`, label: f.label }));
const ALL_KEYS = [...SALARY_KEYS, ...WORK_KEYS].map((k) => k.key);

const PositionVarsModal: React.FC<any> = (props) => {

    const [loading, setLoading] = useState(false);
    const [partName, setPartName] = useState("");
    const [defaults, setDefaults] = useState<{ [key: string]: { value: string; source: string } }>({});
    const [values, setValues] = useState<{ [key: string]: string }>({});
    const [overridden, setOverridden] = useState<{ [key: string]: boolean }>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!props.open || !props.position) {
            return;
        }
        setDefaults({});
        setValues({});
        setOverridden({});
        load();
    }, [props.open, props.position ? props.position.id : 0]);

    const load = async () => {
        try {
            setLoading(true);
            const result: any = await getFetch(`${API}/positions/${props.position.id}/overrides`);
            if (result.message === "success") {
                setPartName(result.partName || "");
                setDefaults(result.defaults || {});
                const ov = result.overrides || {};
                const nextValues: any = {};
                const nextOverridden: any = {};
                ALL_KEYS.forEach((k) => {
                    nextOverridden[k] = ov[k] !== undefined && ov[k] !== null;
                    nextValues[k] = nextOverridden[k] ? String(ov[k]) : "";
                });
                setValues(nextValues);
                setOverridden(nextOverridden);
            } else {
                toast.error(errorText(result, "개인 변수를 불러오지 못했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        try {
            setSaving(true);
            const overrides: any = {};
            ALL_KEYS.forEach((k) => { overrides[k] = overridden[k] ? values[k] : null; });
            const result: any = await postFetch(`${API}/positions/${props.position.id}/overrides`, { overrides });
            if (result.message === "success") {
                toast.success("저장되었습니다", toastOptions);
                props.onSaved();
                props.onClose();
            } else {
                toast.error(errorText(result, "저장에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setSaving(false);
        }
    };

    const renderRow = (k: { key: string; label: string }) => {
        const def = defaults[k.key] || { value: "", source: "" };
        const isOv = !!overridden[k.key];
        return (
            <div key={k.key} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "6px 8px", borderBottom: "1px solid #f0f0f0", fontSize: "13px", fontFamily: "Apple_R" }}>
                <div style={{ width: "140px", flexShrink: 0 }}>{k.label}</div>
                <div style={{ width: "220px", flexShrink: 0, color: "#6b7280" }}>{def.value || "-"} <span className={styles.subText}>({def.source === "common" ? "공통" : "파트"})</span></div>
                <Input
                    size="sm"
                    sx={{ flex: 1, minWidth: 0, backgroundColor: isOv ? "#fff8e1" : undefined }}
                    value={isOv ? values[k.key] : (def.value || "")}
                    onChange={(e) => { const v = e.target.value; setOverridden((prev) => ({ ...prev, [k.key]: true })); setValues((prev) => ({ ...prev, [k.key]: v })); }}
                />
                <div style={{ width: "70px", flexShrink: 0 }}>
                    {isOv && <Button size="sm" variant="outlined" color="neutral" onClick={() => setOverridden((prev) => ({ ...prev, [k.key]: false }))}>기본값</Button>}
                </div>
            </div>
        );
    };

    const p = props.position;

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog sx={{ width: "820px", maxWidth: "95vw", maxHeight: "90vh", overflow: "auto" }}>

                <div className={styles.modalTitle}>
                    {p ? `${p.name} · ${p.branchLabel} · ${partName || p.partName}` : ""} — 변수 조정
                </div>
                <div className={styles.subText} style={{ marginBottom: "10px" }}>
                    회색 = 기본값 그대로 · 노란색 = 이 직원·이 파트만 다르게 저장된 값 · 다음 달 발송에도 그대로 씀 · 지점 정보(대표자·소재지·법인)는 여기서 안 바뀜
                </div>

                {loading
                    ?
                    <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}><CircularProgress /></div>
                    :
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                        <div style={{ display: "flex", gap: "12px", padding: "8px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", color: "#888" }}>
                            <div style={{ width: "140px", flexShrink: 0 }}>항목</div>
                            <div style={{ width: "220px", flexShrink: 0 }}>기본값 (어디서)</div>
                            <div style={{ flex: 1 }}>이 직원 값</div>
                            <div style={{ width: "70px", flexShrink: 0 }}></div>
                        </div>
                        <div style={{ padding: "8px", fontFamily: "Apple_SB", fontSize: "13px", color: "#3d50b0" }}>급여</div>
                        {SALARY_KEYS.map(renderRow)}
                        <div style={{ padding: "8px", fontFamily: "Apple_SB", fontSize: "13px", color: "#3d50b0" }}>근무시간 (파트: {partName || (p ? p.partName : "")})</div>
                        {WORK_KEYS.map(renderRow)}
                    </div>
                }

                <div className={styles.modalButtons}>
                    <div className={styles.hint}>일괄 발송 팝업에서 고친 값도 여기에 똑같이 저장됨</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Button variant="plain" color="neutral" onClick={props.onClose} disabled={saving}>취소</Button>
                        <Button variant="solid" onClick={save} loading={saving} disabled={loading}>저장</Button>
                    </div>
                </div>

            </ModalDialog>
        </Modal>
    );
};

export default PositionVarsModal;
